import type { ReactNode } from 'react'

/**
 * Briques de mise en page.
 *
 * Toutes utilisent les propriétés LOGIQUES de CSS (ms/me, ps/pe, text-start, border-s) et
 * jamais left/right. C'est ce qui fait que l'interface arabe se met en place sans une seule
 * règle dédiée : `dir="rtl"` sur <html> suffit, et les marges partent de l'autre côté.
 */

interface PageHeaderProps {
  title: string
  description?: string
  actions?: ReactNode
}

export function PageHeader({ title, description, actions }: PageHeaderProps) {
  return (
    <div className="pagehead">
      <div>
        <h1>{title}</h1>
        {description !== undefined && <p>{description}</p>}
      </div>
      {actions !== undefined && (
        <>
          <div className="spacer" />
          <div className="split">{actions}</div>
        </>
      )}
    </div>
  )
}

interface CardProps {
  title?: string
  footer?: ReactNode
  children: ReactNode
}

export function Card({ title, footer, children }: CardProps) {
  return (
    <section className="card">
      {title !== undefined && (
        <header>
          <h3>{title}</h3>
        </header>
      )}
      <div className="body">{children}</div>
      {footer !== undefined && <div style={{ padding: 12, borderTop: '1px solid var(--line)', fontSize: 12.5, color: 'var(--muted)' }}>{footer}</div>}
    </section>
  )
}

/**
 * Grille responsive. `min` est la largeur en dessous de laquelle une colonne se replie :
 * on décrit la largeur voulue d'une carte, pas un nombre de colonnes par palier. Le nombre
 * de colonnes se déduit de la place disponible, ce qui évite d'énumérer les points de rupture.
 */
export function Grid({ children, min = '16rem' }: { children: ReactNode; min?: string }) {
  return (
    <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(auto-fill, minmax(${min}, 1fr))` }}>
      {children}
    </div>
  )
}

export function Stack({ children, gap = 4 }: { children: ReactNode; gap?: 2 | 3 | 4 | 6 }) {
  const classes: Record<number, string> = {
    2: 'flex flex-col gap-2',
    3: 'flex flex-col gap-3',
    4: 'flex flex-col gap-4',
    6: 'flex flex-col gap-6',
  }

  return <div className={classes[gap]}>{children}</div>
}

export function Toolbar({ children }: { children: ReactNode }) {
  return <div className="mb-4 flex flex-wrap items-end gap-3">{children}</div>
}

/** Paire libellé/valeur, pour les fiches. Le libellé s'aligne au sens de lecture. */
export function DescriptionItem({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-[0.8rem] text-texte-doux">{label}</dt>
      <dd className="m-0 text-start">{children}</dd>
    </div>
  )
}

export function DescriptionList({ children }: { children: ReactNode }) {
  return (
    <dl className="m-0 grid grid-cols-[repeat(auto-fill,minmax(10rem,1fr))] gap-3">{children}</dl>
  )
}
